/**
 * SDK Principal de Fingerprinting - Arquitetura Refatorada
 * Responsável por orquestrar os serviços de coleta de dados
 */

import { ErrorHandler } from '../handlers/ErrorHandler.js';
import { Logger } from '../utils/Logger.js';
import { ConfigValidator } from '../utils/ConfigValidator.js';
import { CanvasService } from '../services/CanvasService.js';
import { WebGLService } from '../services/WebGLService.js';
import { AudioService } from '../services/AudioService.js';
import { BehavioralService } from '../services/BehavioralService.js';
import { PrivacyService } from '../services/PrivacyService.js';
import { DeviceService } from '../services/DeviceService.js';
import { NetworkService } from '../services/NetworkService.js';
import { CanvasFactory } from '../utils/CanvasFactory.js';

export class FingerprintSDK {
    constructor(config = {}) {
        // Validação e configuração
        this.config = ConfigValidator.validate(config);

        // Inicialização de utilitários
        const debugEnabled = this.config.enableLogging || this.config.debug || false;
        this.logger = new Logger(debugEnabled, 'FingerprintSDK');
        this.errorHandler = new ErrorHandler(this.logger);

        // Inicialização de serviços
        this.services = {
            canvas: new CanvasService(this.config, this.logger),
            webgl: new WebGLService(this.config, this.logger),
            audio: new AudioService(this.config, this.logger),
            behavioral: new BehavioralService(this.config, this.logger),
            privacy: new PrivacyService(this.config, this.logger),
            device: new DeviceService(this.config, this.logger),
            network: new NetworkService(this.config, this.logger)
        };

        // Estado interno
        this.sessionId = this.generateSessionId();
        this.isCollecting = false;
        this.startTime = Date.now();
        this.fingerprint = null;
        this.worker = null;

        this.logger.info('FingerprintSDK inicializado', { sessionId: this.sessionId });

        // Auto-start behavioral analysis immediately if enabled
        if (this.config.behavioralTracking !== false) {
            setTimeout(() => this.startBehavioralCollection(), 100);
        }

        // Auto-generate fingerprint on initialization
        if (this.config.autoGenerate !== false) {
            setTimeout(() => {
                this.generateFingerprint().catch(error => {
                    this.logger.error('Auto fingerprint generation failed:', error);
                });
            }, 200);
        }
    }

    /**
     * Gera ID único da sessão
     */
    generateSessionId() {
        return 'fp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Coleta básica de fingerprint (apenas dados essenciais)
     */
    async collectBasic() {
        if (this.isCollecting) {
            throw new Error('Coleta já em andamento');
        }

        this.isCollecting = true;

        try {
            this.logger.info('Iniciando coleta básica de fingerprint');

            // Coleta apenas dados básicos e rápidos
            const [deviceData, canvasData] = await Promise.allSettled([
                this.services.device.getDeviceInfo(),
                this.services.canvas.getFingerprint()
            ]);

            const fingerprint = {
                session_id: this.sessionId,
                timestamp: Date.now(),
                device: this.extractResult(deviceData),
                canvas: this.extractResult(canvasData),
                version: '2.1.3',
                type: 'basic'
            };

            this.logger.info('Fingerprint básico coletado com sucesso');
            return fingerprint;

        } catch (error) {
            this.errorHandler.handle(error, 'collectBasic');
            throw error;
        } finally {
            this.isCollecting = false;
        }
    }

    /**
     * Alias para collectFingerprint (compatibilidade)
     */
    async collect() {
        return this.collectFingerprint();
    }

    /**
     * Generate comprehensive device fingerprint
     */
    async generateFingerprint() {
        if (this.isCollecting) {
            throw new Error('Coleta já em andamento');
        }

        this.isCollecting = true;

        try {
            this.logger.info('Iniciando geração de fingerprint');

            // Heavy tasks execution (Main Thread - per user request to move Canvas back to SDK)
            // We skip worker execution to avoid OffscreenCanvas issues
            const heavyComponents = await this.generateHeavyComponentsInMainThread();

            // Coleta paralela de dados leves/não-gráficos
            const [deviceData] = await Promise.allSettled([
                this.services.device.getDeviceInfo()
            ]);

            // Coleta sequencial de dados que podem ser dependentes
            const privacyData = await this.services.privacy.detectPrivacyFeatures();
            const networkData = await this.services.network.analyzeNetwork();

            // Extract data from results
            const components = {
                ...heavyComponents,
                device: this.extractResult(deviceData),
                privacy: privacyData,
                network: networkData,
                behavioral: this.services.behavioral.getBehavioralData()
            };

            // Generate hash from components
            const hash = this.hashComponents(components);

            // Create fingerprint object
            this.fingerprint = {
                hash: hash,
                components: components,
                timestamp: Date.now(),
                confidence: this.calculateConfidence(components),
                version: '2.1.3'
            };

            this.logger.info('Fingerprint gerado com sucesso', { hash: hash });

            // Save to localStorage with fpb_ prefix
            this.saveToLocalStorage();

            // Auto-send if enabled
            if (this.config.autoSend) {
                this.sendFingerprint(this.fingerprint).catch(err => {
                    this.logger.error('Auto-send failed', err);
                });
            }

            return this.fingerprint;

        } catch (error) {
            this.errorHandler.handle(error, 'generateFingerprint');
            throw error;
        } finally {
            this.isCollecting = false;
        }
    }

    /**
     * Checks if Web Worker and OffscreenCanvas are supported
     */
    canUseWorker() {
        return typeof Worker !== 'undefined' &&
            CanvasFactory.supportsOffscreenCanvas() &&
            !this.config.disableWorker;
    }

    /**
     * Executes heavy tasks in a Web Worker
     */
    async generateWithWorker() {
        this.logger.info('Delegating heavy tasks to Web Worker');

        return new Promise(async (resolve, reject) => {
            // Initialize worker if needed
            if (!this.worker) {
                try {
                    let workerUrl = this.config.workerUrl || '/sdk/fingerprint.worker.js';

                    // Cross-origin support: Fetch worker code and create Blob URL
                    if (workerUrl.startsWith('http')) {
                        try {
                            const response = await fetch(workerUrl);
                            if (!response.ok) throw new Error(`Failed to fetch worker: ${response.statusText}`);
                            const workerScript = await response.text();
                            const blob = new Blob([workerScript], { type: 'application/javascript' });
                            workerUrl = URL.createObjectURL(blob);
                        } catch (fetchError) {
                            this.logger.warn('Failed to fetch worker script for blob creation, falling back to direct URL', fetchError);
                            // Fallback to direct URL (might fail if CORS)
                        }
                    }

                    this.worker = new Worker(workerUrl, { type: 'module' });

                    this.worker.onmessage = (e) => {
                        const { type, payload } = e.data;

                        if (type === 'FINGERPRINT_GENERATED') {
                            // Resolve the active promise
                            if (this.workerResolve) this.workerResolve(payload);
                        } else if (type === 'ERROR') {
                            if (this.workerReject) this.workerReject(new Error(payload.message));
                        }
                    };

                    this.worker.onerror = (err) => {
                        this.logger.error('Worker error:', err);
                        if (this.workerReject) this.workerReject(err);
                    };
                } catch (e) {
                    return reject(e);
                }
            }

            // Set promise callbacks
            this.workerResolve = resolve;
            this.workerReject = reject;

            // Send command
            this.worker.postMessage({
                type: 'GENERATE_FINGERPRINT',
                payload: {
                    config: JSON.parse(JSON.stringify(this.config)) // Ensure clean JSON
                }
            });

            // Timeout safety
            setTimeout(() => {
                reject(new Error('Worker timeout'));
            }, 5000);
        });
    }

    /**
     * Executes heavy tasks in the main thread (Fallback)
     */
    async generateHeavyComponentsInMainThread() {
        this.logger.info('Executing heavy tasks in Main Thread');

        const [canvasData, webglData, audioData] = await Promise.allSettled([
            this.services.canvas.getFingerprint(),
            this.services.webgl.getFingerprint(),
            this.services.audio.getFingerprint()
        ]);

        return {
            canvas: this.extractResult(canvasData),
            webgl: this.extractResult(webglData),
            audio: this.extractResult(audioData)
        };
    }

    /**
     * Coleta fingerprint completo (legacy compatibility)
     */
    async collectFingerprint() {
        return this.generateFingerprint();
    }

    /**
     * Extrai resultado de Promise.allSettled
     */
    extractResult(settledResult) {
        if (settledResult.status === 'fulfilled') {
            return settledResult.value;
        } else {
            this.logger.warn('Falha na coleta de dados', { error: settledResult.reason });
            return { error: settledResult.reason.message };
        }
    }

    /**
     * Formats fingerprint data to match specific payload requirements
     */
    formatPayload(fingerprint) {
        const components = fingerprint.components || {};
        const device = components.device || {};
        const network = components.network || {};
        const behavioral = components.behavioral || {};
        const canvas = components.canvas || {};
        const webgl = components.webgl || {};
        const audio = components.audio || {};

        // Helper to map mouse movements safely
        const mapMouseMovements = (movements) => {
            if (!Array.isArray(movements)) return [];
            return movements.map(m => ({
                x: Math.round(m.x || 0),
                y: Math.round(m.y || 0),
                timestamp: Math.round(m.timestamp || Date.now()),
                event_type: 'move'
            }));
        };

        // Helper to map keyboard dynamics safely
        const mapKeyboardDynamics = (keystrokes) => {
            if (!Array.isArray(keystrokes)) return [];
            return keystrokes.map(k => ({
                key_code: k.code || '',
                dwell_time: Math.round(k.duration || 0),
                flight_time: 0, // Not captured currently
                timestamp: Math.round(k.timestamp || Date.now())
            }));
        };

        // Helper to map touch events safely
        const mapTouchEvents = (touches) => {
            if (!Array.isArray(touches)) return [];
            return touches.map(t => ({
                x: Math.round(t.x || 0),
                y: Math.round(t.y || 0),
                pressure: t.force || 0,
                radius: 0, // Not explicitly captured in same format
                event_type: t.type || 'unknown',
                timestamp: Math.round(t.timestamp || Date.now())
            }));
        };

        const rawEvents = {
            mouse: behavioral.events?.mouse || {},
            keyboard: behavioral.events?.keyboard || {},
            touch: behavioral.events?.touch || {}
        };

        // Create a deep copy of components to avoid mutating original and to allow structural adjustments
        const payloadComponents = JSON.parse(JSON.stringify(components));

        // Flatten device.basic into device root for backend compatibility
        const deviceData = { ...device };
        if (deviceData.basic) {
            deviceData.platform = deviceData.basic.platform;
            deviceData.userAgent = deviceData.basic.userAgent;
            deviceData.language = deviceData.basic.language;
            delete deviceData.basic; // Remove redundant nested object
        }

        // Prepare Geolocation Data (Coordinates + IP info + Connection)
        // Avoid circular references or massive objects
        const geolocationData = {
            ip: network.ips?.public?.[0] || network.ips?.webRTC?.[0] || '',
            network_type: network.connection?.type || 'unknown',

            // Coordinates (from Navigator or IP fallback)
            latitude: network.geolocation?.latitude || network.geolocation?.coordinates?.latitude || null,
            longitude: network.geolocation?.longitude || network.geolocation?.coordinates?.longitude || null,
            accuracy: network.geolocation?.accuracy || network.geolocation?.coordinates?.accuracy || null,

            // Location Details (from IP API)
            city: network.geolocation?.city || null,
            region: network.geolocation?.region || null,
            country: network.geolocation?.country || network.geolocation?.country_name || null,
            org: network.geolocation?.org || null,
            timezone: network.geolocation?.timezone || null,

            // Merge any other keys that might be in the geolocation object
            ...network.geolocation
        };

        return {
            audio_data: audio.hash || (audio.status === 'skipped_no_interaction' ? 'skipped' : (audio.error ? `Error: ${audio.error}` : '')),
            behavioral: {
                events: {
                    mouseEvents: rawEvents.mouse.movements || [],
                    keyboardEvents: rawEvents.keyboard.keystrokes || [],
                    touchEvents: rawEvents.touch.touches || [],
                    clickEvents: rawEvents.mouse.clicks || [],
                    scrollEvents: rawEvents.mouse.scrolls || []
                },
                sessionDuration: Date.now() - this.startTime,
                stats: this.getBehavioralStats()
            },
            canvas_data: canvas.hash || (canvas.error ? `Error: ${canvas.error}` : ''),
            // components: payloadComponents, // REMOVED to avoid redundancy as requested
            confidence: fingerprint.confidence,
            fingerprint_hash: fingerprint.hash,
            geolocation: geolocationData,
            ip_address: network.ips?.public?.[0] || network.ips?.webRTC?.[0] || '',
            keyboard_dynamics: mapKeyboardDynamics(rawEvents.keyboard.keystrokes),
            language: deviceData.language || '', // Use flattened device data
            mouse_movements: mapMouseMovements(rawEvents.mouse.movements),
            platform: deviceData.platform || '', // Use flattened device data
            referrer: typeof document !== 'undefined' ? document.referrer : '',
            screen_resolution: device.screen ? `${device.screen.width}x${device.screen.height}` : '',
            session_id: this.sessionId,
            timestamp: fingerprint.timestamp,
            timezone_offset: device.location?.timezone?.offset || 0,
            touch_events: mapTouchEvents(rawEvents.touch.touches),
            url: typeof window !== 'undefined' ? window.location.href : '',
            user_agent: deviceData.userAgent || '', // Use flattened device data
            version: fingerprint.version,
            webgl_fingerprint: webgl.hash || (webgl.error ? `Error: ${webgl.error}` : ''),
            webgl_renderer: webgl.renderer || 'Unknown Renderer',
            webgl_vendor: webgl.vendor || 'Unknown Vendor'
        };
    }

    /**
     * Envia fingerprint para o backend
     */
    async sendFingerprint(data = null) {
        try {
            // Use provided data, or cached fingerprint. 
            // Only collect if strictly necessary (and be careful about recursion).
            let fingerprint = data || this.fingerprint;

            if (!fingerprint) {
                this.logger.info('Nenhum fingerprint fornecido ou em cache, gerando novo...');
                fingerprint = await this.collectFingerprint();
            }

            // Format payload before sending
            const payload = this.formatPayload(fingerprint);

            const headers = {
                'Content-Type': 'application/json',
                'X-Customer-ID': this.config.customerId?.toString() || ''
            };

            // Add API Token if present
            if (this.config.apiToken) {
                headers['X-API-Token'] = this.config.apiToken;
            }

            const response = await fetch(this.config.apiUrl, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            this.logger.info('Fingerprint enviado com sucesso', { result });

            return result;

        } catch (error) {
            this.errorHandler.handle(error, 'sendFingerprint');
            throw error;
        }
    }

    /**
     * Inicia coleta comportamental
     */
    startBehavioralCollection() {
        this.services.behavioral.startCollection();
        this.logger.info('Coleta comportamental iniciada');
    }

    /**
     * Para coleta comportamental
     */
    stopBehavioralCollection() {
        this.services.behavioral.stopCollection();
        this.logger.info('Coleta comportamental parada');
    }

    /**
     * Limpa dados comportamentais
     */
    clearBehavioralData() {
        this.services.behavioral.clearData();
        this.logger.info('Dados comportamentais limpos');
    }

    /**
     * Obtém dados comportamentais atuais
     */
    getBehavioralData() {
        return this.services.behavioral.getBehavioralData();
    }

    /**
     * Obtém informações de configuração
     */
    getConfig() {
        return { ...this.config };
    }

    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        const validatedConfig = ConfigValidator.validate({ ...this.config, ...newConfig });
        this.config = validatedConfig;

        // Atualiza configuração nos serviços
        Object.values(this.services).forEach(service => {
            if (service.updateConfig) {
                service.updateConfig(this.config);
            }
        });

        this.logger.info('Configuração atualizada');
    }

    /**
     * Obtém estatísticas de performance
     */
    getPerformanceStats() {
        const stats = {};

        Object.entries(this.services).forEach(([name, service]) => {
            if (service.getStats) {
                stats[name] = service.getStats();
            }
        });

        return stats;
    }

    /**
     * Alias para getPerformanceStats (compatibilidade)
     */
    getStats() {
        return this.getPerformanceStats();
    }

    /**
     * Hash all components to create fingerprint
     * Only includes stable device characteristics, excludes temporal data
     */
    hashComponents(components) {
        // Clean components to remove temporal/variable data
        const stableComponents = this.extractStableComponents(components);

        // Sort keys to ensure consistent ordering
        const sortedKeys = Object.keys(stableComponents).sort();
        const sortedComponents = {};
        sortedKeys.forEach(key => {
            sortedComponents[key] = stableComponents[key];
        });

        // Serialize with sorted keys
        const combined = JSON.stringify(sortedComponents);
        return this.simpleHash(combined);
    }

    /**
     * Extract only stable device characteristics for hashing
     * Removes timestamps, IPs, sessions, and other variable data
     */
    extractStableComponents(components) {
        const stable = {};

        // Canvas - keep only hash (stable characteristic)
        if (components.canvas) {
            stable.canvas = {
                hash: components.canvas.hash || '',
                pixel_checksum: components.canvas.pixel_checksum || 0
            };
        }

        // WebGL - keep stable characteristics
        if (components.webgl) {
            stable.webgl = {
                hash: components.webgl.hash || '',
                vendor: components.webgl.vendor || '',
                renderer: components.webgl.renderer || '',
                version: components.webgl.version || '',
                glsl_version: components.webgl.glsl_version || ''
            };
        }

        // Audio - keep only hash
        if (components.audio) {
            stable.audio = {
                hash: components.audio.hash || ''
            };
        }

        // Device - keep stable characteristics only
        if (components.device) {
            stable.device = {
                userAgent: components.device.basic?.userAgent || components.device.userAgent || '',
                platform: components.device.basic?.platform || components.device.platform || '',
                language: components.device.basic?.language || components.device.language || '',
                screenResolution: components.device.screen ?
                    `${components.device.screen.width}x${components.device.screen.height}` : '',
                colorDepth: components.device.screen?.colorDepth || 0,
                pixelRatio: components.device.screen?.pixelRatio || 1,
                hardwareConcurrency: components.device.hardware?.concurrency || 0,
                deviceMemory: components.device.hardware?.memory || 0,
                maxTouchPoints: components.device.hardware?.maxTouchPoints || 0,
                timezone: components.device.location?.timezone?.name || ''
            };
        }

        // Privacy - keep only detection flags (not dynamic data)
        if (components.privacy) {
            stable.privacy = {
                detected: components.privacy.detected || false
            };
        }

        // Network - EXCLUDE (IPs change, connection type varies)
        // Behavioral - EXCLUDE (timestamps and session data)

        return stable;
    }

    /**
     * Simple hash function for fingerprint generation
     */
    simpleHash(str) {
        let hash = 0;
        if (str.length === 0) return hash.toString(16);

        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }

        return Math.abs(hash).toString(16);
    }

    /**
     * Calculate confidence score based on collected components
     */
    calculateConfidence(components) {
        let score = 0;
        let total = 0;

        const weights = {
            canvas: 20,
            webgl: 15,
            audio: 10,
            device: 15,
            privacy: 10,
            network: 10,
            behavioral: 20
        };

        Object.keys(weights).forEach(key => {
            total += weights[key];
            if (components[key] && components[key].error === undefined) {
                score += weights[key];
            }
        });

        return Math.round((score / total) * 100);
    }

    /**
     * Save fingerprint to localStorage with fpb_ prefix
     */
    saveToLocalStorage() {
        if (!this.fingerprint) return;

        try {
            const key = `fpb_${this.fingerprint.hash}`;
            const data = {
                fingerprint: this.fingerprint.hash,
                timestamp: Date.now(),
                version: '2.0.0',
                components: this.fingerprint.components,
                behavioral: {
                    events: this.services.behavioral.getBehavioralData(),
                    sessionDuration: Date.now() - this.startTime,
                    stats: this.getBehavioralStats()
                }
            };

            localStorage.setItem(key, JSON.stringify(data));

            this.logger.info('Fingerprint saved to localStorage', { key: key });
        } catch (error) {
            this.logger.error('Error saving to localStorage:', error);
        }
    }

    /**
     * Get behavioral statistics
     */
    getBehavioralStats() {
        const behavioralData = this.services.behavioral.getBehavioralData();

        return {
            mouseEvents: behavioralData.mouse?.length || 0,
            keyboardEvents: behavioralData.keyboard?.length || 0,
            touchEvents: behavioralData.touch?.length || 0,
            clickEvents: behavioralData.clicks?.length || 0,
            scrollEvents: behavioralData.scroll?.length || 0,
            sessionDuration: Date.now() - this.startTime
        };
    }

    /**
     * Send fingerprint for analysis
     */
    async sendForAnalysis(fingerprint = null) {
        const data = fingerprint || this.fingerprint;

        if (!data) {
            throw new Error('No fingerprint data to send');
        }

        return fetch(this.config.apiEndpoint || this.config.apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Fingerprint-Version': '2.0.0'
            },
            body: JSON.stringify({
                ...data,
                behavioral: {
                    events: this.services.behavioral.getBehavioralData(),
                    sessionDuration: Date.now() - this.startTime,
                    stats: this.getBehavioralStats()
                },
                url: typeof window !== 'undefined' ? window.location.href : '',
                referrer: typeof document !== 'undefined' ? document.referrer : ''
            })
        }).then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
        });
    }

    /**
     * Limpa recursos e para coletas
     */
    destroy() {
        this.stopBehavioralCollection();

        Object.values(this.services).forEach(service => {
            if (service.destroy) {
                service.destroy();
            }
        });

        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }

        this.logger.info('SDK destruído');
    }
}

// Exportação para compatibilidade
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { FingerprintSDK };
} else {
    window.FingerprintSDK = FingerprintSDK;
}